import { useMyUser } from '@/app/hooks/data-hooks/useMyUser'
import { IApiIndividualResponse } from '@/types/api'
import { IManagerContact } from '@/types/email-name-pair.interface'
import {
  EnquiryContext,
  EnquiryFeature,
  EnquiryStatus,
  IEnquiry,
  IEnquiryCreatePayload,
} from '@/types/enquiry'
import deepEqual from 'fast-deep-equal'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useState } from 'react'
import { useNotificationContext } from '../../components/Notification'
import { useEnquiries } from '../../hooks/data-hooks/useEnquiries'
import { useEnquiryMutation } from '../../hooks/data-hooks/useEnquiryMutation'
import {
  EnquiryConfirmationModal,
  EnquiryConfirmationModalCloseReason,
} from './EnquiryConfirmationModal'

type Nullable<T> = { [K in keyof T]: T[K] | null }

export type CoursePageBaseContext = {
  course: string
  cohort: string
  educationProvider: string
  company: string | null
}

type EnquiriesContextValue = {
  enquiries: IEnquiry[] | null
  isDisabled: boolean
  isLoading: boolean
  baseContext: Nullable<CoursePageBaseContext>
  isEnquiryRequested(
    featureRequest: EnquiryFeature,
    context: EnquiryContext
  ): boolean | undefined
  sendEnquiry: (
    enquiry?: IEnquiryCreatePayload
  ) => Promise<IApiIndividualResponse<IEnquiry> | undefined>
}

const EnquiriesContext = React.createContext<EnquiriesContextValue>({
  enquiries: null,
  isDisabled: true,
  isLoading: false,
  baseContext: {
    course: null,
    cohort: null,
    educationProvider: null,
    company: null,
  },
  isEnquiryRequested: () => undefined,
  sendEnquiry: async () => ({}) as IApiIndividualResponse<IEnquiry>,
})

type EnquiriesContextProviderProps = {
  baseContext: CoursePageBaseContext
  children: React.ReactNode
  onModalOpen?: () => void
}

export const EnquiriesContextProvider = ({
  baseContext,
  children,
  onModalOpen,
}: EnquiriesContextProviderProps) => {
  const { data: { data: user } = {}, isLoading: isLoadingUserInfo } =
    useMyUser()
  const { setMessage } = useNotificationContext()
  const t = useTranslations('Enquiries')

  const [enquiryToCreate, setEnquiryToCreate] =
    useState<IEnquiryCreatePayload | null>(null)
  const isConfirmationModalOpen = !!enquiryToCreate

  const {
    data: { data: enquiries = null } = {},
    isLoading: isLoadingEnquiries,
  } = useEnquiries(
    {
      reporter: user?._id,
    },
    {
      limit: 1000000,
    },
    { enabled: !!user?._id }
  )
  const { mutateAsync, isLoading: isProcessing } = useEnquiryMutation()

  const sendEnquiry = async (
    enquiry: IEnquiryCreatePayload,
    userConfirmed?: boolean,
    managerContact?: IManagerContact
  ) => {
    if (!userConfirmed) {
      setEnquiryToCreate(enquiry)
      return
    }

    try {
      return await mutateAsync({
        ...enquiry,
        context: {
          ...enquiry.context,
          ...(managerContact &&
          (enquiry.featureRequest === EnquiryFeature.PaymentMethod ||
            enquiry.featureRequest === EnquiryFeature.PaymentFeature)
            ? {
                managerContact: {
                  email: managerContact.email,
                  role: managerContact.role,
                },
              }
            : {}),
        } as EnquiryContext,
      })
    } catch (error) {
      setMessage(t('enquiry-error'))
    }

    setEnquiryToCreate(null)
  }

  const isEnquiryRequested = useCallback(
    (featureRequest: EnquiryFeature, context: EnquiryContext) => {
      return enquiries?.some((enquiry) => {
        let filteredContext: EnquiryContext | undefined

        if (
          enquiry.featureRequest === EnquiryFeature.PaymentMethod ||
          enquiry.featureRequest === EnquiryFeature.PaymentFeature
        ) {
          filteredContext = Object.fromEntries(
            Object.entries(enquiry.context ?? {}).filter(
              ([key]) => key !== 'managerContact'
            )
          ) as EnquiryContext
        } else {
          filteredContext = enquiry.context
        }

        return (
          enquiry.featureRequest === featureRequest &&
          ![EnquiryStatus.Resolved, EnquiryStatus.Cancelled].includes(
            enquiry.status
          ) &&
          deepEqual(filteredContext, context)
        )
      })
    },
    [enquiries]
  )

  const isLoading = isLoadingUserInfo || isLoadingEnquiries || isProcessing
  const isDisabled = !isLoading && !user?.company

  useEffect(() => {
    if (isConfirmationModalOpen) {
      onModalOpen?.()
    }
  }, [isConfirmationModalOpen, onModalOpen])

  return (
    <EnquiriesContext.Provider
      value={{
        enquiries,
        isLoading,
        isDisabled,
        baseContext,
        isEnquiryRequested,
        sendEnquiry,
      }}
    >
      <EnquiryConfirmationModal
        enquiry={enquiryToCreate}
        isOpen={!!isConfirmationModalOpen}
        onClose={async (
          reason: EnquiryConfirmationModalCloseReason,
          managerContact?: IManagerContact
        ) => {
          if (reason === EnquiryConfirmationModalCloseReason.CONFIRM) {
            await sendEnquiry(enquiryToCreate!, true, managerContact)
          }

          setEnquiryToCreate(null)
        }}
      />
      {children}
    </EnquiriesContext.Provider>
  )
}

export function useEnquiriesExperience() {
  const context = React.useContext(EnquiriesContext)

  return context
}
